import type {
  MachineAttachmentItemDto,
  MachineAttachmentItemPriceOnlyDto
} from '@api/machines'
import { useTranslation } from 'react-i18next'
import { BreakdownItem } from '../breakdown-item/BreakdownItem'
import { BreakdownSection } from '../breakdown-section/BreakdownSection'

type MachineAttachmentData = MachineAttachmentItemDto & {
  oldPrice?: number | null
}

const AttachmentsSection = ({
  isMachine,
  keyPrefix,
  machineAttachments,
  attachmentsOldPrices
}: {
  isMachine: boolean
  machineAttachments: MachineAttachmentItemDto[]
  attachmentsOldPrices: MachineAttachmentItemPriceOnlyDto[]
  keyPrefix: string
}) => {
  const { t } = useTranslation('', {
    keyPrefix
  })

  const comparePrices = (
    currentData: MachineAttachmentItemDto[],
    oldData: MachineAttachmentItemPriceOnlyDto[]
  ) => {
    return currentData.map((currentItem: MachineAttachmentItemDto) => {
      const oldItem = oldData.find(
        (item: MachineAttachmentItemPriceOnlyDto) => item.id === currentItem.id
      )

      if (currentItem.priceChanged && oldItem) {
        return {
          ...currentItem,
          oldPrice: oldItem.price
        }
      }

      return currentItem
    })
  }

  const machineAttachmentData = comparePrices(
    machineAttachments,
    attachmentsOldPrices
  )

  const calculateTotalPrice = (
    pricesList: MachineAttachmentData[],
    current: boolean
  ) => {
    return pricesList.reduce((sum, item) => {
      const priceValue =
        !current && item.priceChanged && item.oldPrice
          ? item.oldPrice
          : item.price

      return sum + (priceValue ?? 0) * (item.quantity ?? 0)
    }, 0)
  }

  const attachmentsSectionCurrentTotal = calculateTotalPrice(
    machineAttachmentData,
    true
  )
  const attachmentsSectionOldTotal = calculateTotalPrice(
    machineAttachmentData,
    false
  )

  return isMachine && machineAttachmentData.length > 0 ? (
    <BreakdownSection
      price={attachmentsSectionCurrentTotal}
      title={`+ ${t('attachments')} (${machineAttachmentData.length})`}
      oldPrice={
        attachmentsSectionOldTotal !== 0 &&
        attachmentsSectionOldTotal !== attachmentsSectionCurrentTotal
          ? attachmentsSectionOldTotal
          : undefined
      }
    >
      {machineAttachmentData.map((attachment: MachineAttachmentData) => {
        const newPrice = (attachment.price ?? 0) * (attachment.quantity ?? 0)
        const oldPrice = (attachment.oldPrice ?? 0) * (attachment.quantity ?? 0)
        return (
          <BreakdownItem
            name={`${attachment.modelName}`}
            key={`spec_breakdown_attachment_${attachment.quotationItemId}`}
            price={newPrice}
            oldPrice={oldPrice !== 0 ? oldPrice : undefined}
          />
        )
      })}
    </BreakdownSection>
  ) : null
}

export { AttachmentsSection }
